import {
  AddFieldPayload,
  CreateFieldVisitNotePayload,
  CreateFieldVisitPayload,
  CreateFieldVisitPlanPayload,
  FieldObjStore,
  FieldVisitHistoricalObjStore,
  FieldVisitNoteObjStore,
  FieldVisitObjStore,
  FieldVisitPlanObjStore,
  FieldVisitProgressHistoricalObjStore,
  FieldVisitProgressObjStore,
  LocalVisitProgress,
  UpdateFieldPayload,
  UpdateFieldVisitPayload,
  UpdateFieldVisitPlanPayload
} from '@mytypes/local';
import moment from 'moment';

import { db } from '../../src/db';

// FieldVisitObjStore
export const getAllFieldVisitsWithDexie = async (): Promise<FieldVisitObjStore[]> => {
  return await db.fieldVisits.toArray();
};

export const getAllHistoricalFieldVisitsWithDexie = async (): Promise<FieldVisitHistoricalObjStore[]> => {
  return await db.fieldVisitsHistorical.toArray();
};

export const getAllHistoricalFieldVisitsProgressWithDexie = async (): Promise<FieldVisitProgressHistoricalObjStore[]> => {
  return await db.fieldVisitsProgressHistorical.toArray();
};

export const getFieldVisitWithDexie = async (id: number): Promise<FieldVisitObjStore | undefined> => {
  return await db.fieldVisits.get(id);
};

export const getFieldVisitForWizardWithDexie = async (fieldId: string, protocolId: string, stepId: string): Promise<FieldVisitObjStore | undefined> => {
  try {
    const fieldVisit = await db.fieldVisits
      .where('fieldId')
      .equals(fieldId)
      .and(item => item.protocolId === protocolId)
      .and(item => item.stepId === stepId)
      .first();

    return fieldVisit;
  } catch (error) {
    console.error(error);
    return undefined;
  }
};

export const getFieldVisitsByFieldIdWithDexie = async (fieldId: string): Promise<FieldVisitObjStore[]> => {
  try {
    const fieldVisits = await db.fieldVisits.where('fieldId').equals(fieldId).toArray();

    return fieldVisits;
  } catch (error) {
    console.error(error);
    throw new Error('An error occurred in getFieldVisitsByFieldIdWithDexie');
  }
};

export const createFieldVisitWithDexie = async (payload: CreateFieldVisitPayload): Promise<number | undefined> => {
  const now = moment().toISOString();

  const id = await db.fieldVisits.add({
    ...payload,
    updatedAt: now,
    createdAt: now
  });

  await upsertHistoricalFieldVisit(id).finally(cleanUpHistoricalFieldVisits);

  return id;
};

export const updateFieldVisitWithDexie = async (id: number, payload: UpdateFieldVisitPayload): Promise<number | undefined> => {
  const response = await db.fieldVisits.update(id, {
    ...payload,
    updatedAt: moment().toISOString()
  });

  await upsertHistoricalFieldVisit(id).finally(cleanUpHistoricalFieldVisits);

  return response;
};

const upsertHistoricalFieldVisit = async (fieldVisitId: number): Promise<void> => {
  try {
    const fieldVisit = await db.fieldVisits.get(fieldVisitId);

    if (!fieldVisit) {
      console.error('Could not find field visit record with given id to create historical record for', fieldVisitId);
      return;
    }

    const now = moment().toISOString();
    const existingRecord = await db.fieldVisitsHistorical
      .where('[clientId+fieldId+stepId+trialId]')
      .equals([fieldVisit.clientId, fieldVisit.fieldId, fieldVisit.stepId, fieldVisit.trialId])
      .first();

    const { id: fieldVisitTableId, ...updatePayload } = fieldVisit;

    if (existingRecord?.id !== undefined) {
      await db.fieldVisitsHistorical.update(existingRecord.id, {
        ...updatePayload,
        lastTouched: now
      });

      return;
    }

    await db.fieldVisitsHistorical.add({
      ...updatePayload,
      lastTouched: now
    });
  } catch (error) {
    console.error('Failed to create historical record for field visit', error);
  }
};

export const cleanUpHistoricalFieldVisits = async () => {
  const maxAgeDays = 30;
  const maxRecords = 50;
  try {
    const cutoffDate = moment().subtract(maxAgeDays, 'days').toISOString();

    await db.fieldVisitsHistorical.where('lastTouched').below(cutoffDate).delete();

    const recordsToDelete = await db.fieldVisitsHistorical.orderBy('lastTouched').reverse().offset(maxRecords).primaryKeys();

    if (recordsToDelete.length) {
      await db.fieldVisitsHistorical.bulkDelete(recordsToDelete);
    }
  } catch (error) {
    console.error('Failed to clean up historical records', error);
  }
};

export const markUploadsAsUploadedWithDexie = async (visitId: number, localValueIds: string[]): Promise<number | undefined> => {
  return db.transaction('rw', db.fieldVisits, async () => {
    const visit = await db.fieldVisits.get(visitId);

    if (!visit) return;

    return await db.fieldVisits.update(visitId, {
      completedUploads: [...(visit.completedUploads ? visit.completedUploads : []), ...localValueIds],
      updatedAt: moment().toISOString()
    });
  });
};

export const deleteFieldVisitWithDexie = async (id: number): Promise<void> => {
  return await db.fieldVisits.delete(id);
};

// FieldVisitProgressObjStore
export const getFieldVisitProgressWithDexie = async (): Promise<FieldVisitProgressObjStore | undefined> => {
  return await db.fieldVisitsProgress.toCollection().first();
};

export const setFieldVisitProgressWithDexie = async (localAutoSaveVisit: LocalVisitProgress): Promise<void> => {
  const progress = await db.fieldVisitsProgress.toCollection().first();
  await db.fieldVisitsProgress.put({ ...(progress ? { id: progress.id } : {}), ...localAutoSaveVisit, lastUpdated: moment().toISOString() });
  await upsertHistoricalFieldVisitProgress().finally(cleanUpHistoricalFieldVisitProgress);
};

export const setFieldVisitProgressLastSavedWithDexie = async (lastSaved: string): Promise<void> => {
  const progress = await db.fieldVisitsProgress.toCollection().first();
  if (progress) {
    await db.fieldVisitsProgress.update(progress.id, { lastSaved });
    await upsertHistoricalFieldVisitProgress().finally(cleanUpHistoricalFieldVisitProgress);
  }
};

export const clearFieldVisitProgressWithDexie = async (): Promise<void> => {
  await db.fieldVisitsProgress.clear();
};

const upsertHistoricalFieldVisitProgress = async (): Promise<void> => {
  try {
    const fieldVisitProgress = await db.fieldVisitsProgress.toCollection().first();

    if (!fieldVisitProgress) {
      console.error('Could not find field visit progress  to create historical record for');
      return;
    }

    const now = moment().toISOString();
    const existingRecord = await db.fieldVisitsProgressHistorical
      .where('[fieldId+stepId+trialId]')
      .equals([fieldVisitProgress.fieldId, fieldVisitProgress.stepId, fieldVisitProgress.trialId])
      .first();

    const { id: fieldVisitProgressTableId, boundaries, ...updatePayload } = fieldVisitProgress;

    if (existingRecord?.id !== undefined) {
      await db.fieldVisitsProgressHistorical.update(existingRecord.id, {
        ...updatePayload,
        boundaries: null,
        lastTouched: now
      });

      return;
    }

    await db.fieldVisitsProgressHistorical.add({
      ...updatePayload,
      boundaries: null,
      lastTouched: now
    });
  } catch (error) {
    console.error('Failed to create historical record for field visit progress', error);
  }
};

export const cleanUpHistoricalFieldVisitProgress = async () => {
  const maxAgeDays = 30;
  const maxRecords = 50;
  try {
    const cutoffDate = moment().subtract(maxAgeDays, 'days').toISOString();

    await db.fieldVisitsProgressHistorical.where('lastTouched').below(cutoffDate).delete();

    const recordsToDelete = await db.fieldVisitsProgressHistorical.orderBy('lastTouched').reverse().offset(maxRecords).primaryKeys();

    if (recordsToDelete.length) {
      await db.fieldVisitsProgressHistorical.bulkDelete(recordsToDelete);
    }
  } catch (error) {
    console.error('Failed to clean up progress historical records', error);
  }
};

// FieldVisitNoteObjStore
export const getAllFieldVisitsNotesWithDexie = async (): Promise<FieldVisitNoteObjStore[]> => {
  return await db.fieldVisitNotes.toArray();
};

export const createFieldVisitNoteWithDexie = async (payload: CreateFieldVisitNotePayload): Promise<number | undefined> => {
  const now = moment().toISOString();

  const existing = await db.fieldVisitNotes.where('localId').equals(payload.localId).first();

  if (existing) {
    await db.fieldVisitNotes.update(existing.id!, {
      ...payload,
      updatedAt: now
    });

    return existing.id;
  }

  return await db.fieldVisitNotes.add({
    ...payload,
    createdAt: now,
    updatedAt: now
  });
};

export const deleteFieldVisitNoteWithDexie = async (id: number): Promise<void> => {
  return await db.fieldVisitNotes.delete(id);
};

// FieldVisitPlanObjStore
export const getAllFieldVisitPlansWithDexie = async (): Promise<FieldVisitPlanObjStore[]> => {
  return await db.fieldVisitPlans.toArray();
};

export const getFieldVisitPlanWithDexie = async (id: number): Promise<FieldVisitPlanObjStore | undefined> => {
  return await db.fieldVisitPlans.get(id);
};

export const createFieldVisitPlanWithDexie = async (payload: CreateFieldVisitPlanPayload): Promise<number | undefined> => {
  return await db.fieldVisitPlans.add({
    ...payload,
    updatedAt: moment().toISOString(),
    createdAt: moment().toISOString()
  });
};

export const updateFieldVisitsPlanWithDexie = async (id: number, payload: UpdateFieldVisitPlanPayload): Promise<number | undefined> => {
  await db.fieldVisitPlans.update(id, {
    ...payload,
    updatedAt: moment().toISOString()
  });

  return id;
};

export const marksFieldVisitPlansNotDownloaded = async (id: number): Promise<number | undefined> => {
  const plan = await db.fieldVisitPlans.get(id);
  if (!plan) return undefined;

  const fields = plan.fields.map(f => ({ ...f, downloaded: false }));

  await db.fieldVisitPlans.update(id, {
    fields,
    updatedAt: moment().toISOString()
  });

  return id;
};

export const markFieldVisitPlanFieldDownloaded = async (planId: number, fieldId: string, protocolId: string): Promise<number | undefined> => {
  const plan = await db.fieldVisitPlans.get(planId);
  if (!plan) return undefined;

  const fields = plan.fields.map(f => {
    if (f.fieldId === fieldId && f.protocolId === protocolId) {
      return {
        ...f,
        downloaded: true
      };
    } else {
      return f;
    }
  });

  await db.fieldVisitPlans.update(planId, {
    fields,
    updatedAt: moment().toISOString()
  });

  return planId;
};

export const deleteFieldVisitsPlanWithDexie = async (id: number): Promise<void> => {
  const plan = await getFieldVisitPlanWithDexie(Number(id));
  if (!plan) return;

  for (let i = 0; i < plan.fields.length; i++) {
    const field = plan.fields[i];
    const fieldObj = await getFieldByProtocolWithDexie(field.fieldId, field.protocolId);
    if (!fieldObj) continue;

    const fieldObjPlans = fieldObj.plans?.filter((p: any) => Number(p.id) !== id);
    if (fieldObjPlans?.length) {
      await updateFieldWithDexie(fieldObj.id, { plans: fieldObjPlans });
    } else {
      await deleteFieldWithDexie(fieldObj.id);
    }
  }

  return await db.fieldVisitPlans.delete(id);
};

// FieldObjStore
export const getAllFieldsWithDexie = async (): Promise<FieldObjStore[]> => {
  return await db.fields.toArray();
};

export const getFieldByProtocolWithDexie = async (fieldId: string, protocolId: string): Promise<FieldObjStore | undefined> => {
  try {
    const field = await db.fields
      .where('fieldId')
      .equals(fieldId)
      .and(item => item.protocolId === protocolId)
      .first();
    return field;
  } catch (error) {
    console.error(error);
    return undefined;
  }
};

export const getFieldByGrowerWithDexie = async (growerId: string): Promise<FieldObjStore[]> => {
  try {
    const field = await db.fields.where('growerId').equals(growerId).toArray();
    return field;
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const getFieldWithDexie = async (id: number): Promise<FieldObjStore | undefined> => {
  return await db.fields.get(id);
};

export const addFieldWithDexie = async (payload: AddFieldPayload): Promise<void> => {
  const field = await getFieldByProtocolWithDexie(payload.fieldId, payload.protocolId);
  if (field) {
    const newPlans = field.plans.some((item: any) => item.id === payload.planId)
      ? field.plans
      : [...field.plans, { id: payload.planId, name: payload.planName }];
    await updateFieldWithDexie(field.id, { ...payload, plans: newPlans });
  } else {
    await db.fields.add({
      ...payload,
      plans: [{ id: payload.planId, name: payload.planName }],
      updatedAt: moment().toISOString(),
      createdAt: moment().toISOString()
    });
  }
};

export const updateFieldWithDexie = async (id: number, payload: UpdateFieldPayload): Promise<number | undefined> => {
  return await db.fields.update(id, {
    ...payload,
    updatedAt: moment().toISOString()
  });
};

export const deleteFieldWithDexie = async (id: number): Promise<void> => {
  return await db.fields.delete(id);
};
